import React, { useCallback, useEffect, useState } from 'react';
import { geoguesserService } from '../../../services/api';
import MapDetailsForm from './MapDetailsForm';
import FloorManager from './FloorManager';
import LevelAssigner from './LevelAssigner';
import './GeoMapEditor.css';

/**
 * Full-surface editor for a single GeoGuesser map, composed of
 * three self-contained sub-components:
 *
 *   1. MapDetailsForm  – map-level attributes with auto-save
 *   2. FloorManager    – floors list with DnD + auto-save
 *   3. LevelAssigner   – per-level floor reassignment + pin picker
 *
 * The editor coordinates shared state (the floors array, the map
 * object) so the sub-components stay loosely coupled but always
 * consistent. Nothing is blocked behind a Save button.
 *
 * Props:
 *   - `mapId`        – required.
 *   - `title`        – optional header title (e.g. "Edit map").
 *   - `onClose`      – optional callback; renders a close button in
 *                      the header when provided.
 *   - `footer`       – optional render prop for page-level CTAs
 *                      (the migration wizard uses this to render its
 *                      "Mark migration complete" button).
 *   - `enableLevelDraft` – propagates to LevelAssigner.
 *
 * Error handling: errors from any sub-component bubble into a
 * dismissable banner at the top of the editor.
 */
const GeoMapEditor = ({
  mapId,
  title = 'Edit map',
  onClose,
  footer,
  enableLevelDraft = true,
}) => {
  const [map, setMap] = useState(null);
  const [floors, setFloors] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchVersion, setFetchVersion] = useState(0);

  // Keep the latest buffers from FloorManager accessible to
  // LevelAssigner so live-edited names show up in the target-floor
  // dropdown before the PATCH round-trips.
  const [floorBuffers, setFloorBuffers] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await geoguesserService.getMapById(mapId);
        if (cancelled) return;
        setMap(res.data);
        // Pre-seed floors from the map response so LevelAssigner's
        // target-floor dropdown and its main preview canvas render
        // correctly on the very first frame. Without this there's a
        // visible flash where the target floor can't be resolved
        // against an empty floors list.
        if (Array.isArray(res.data?.floors)) {
          const sorted = [...res.data.floors].sort(
            (a, b) => (a.order ?? 0) - (b.order ?? 0),
          );
          setFloors(sorted);
          setFloorBuffers((prev) => {
            const next = { ...prev };
            for (const f of sorted) {
              if (!next[f.id]) {
                next[f.id] = {
                  name: f.name,
                  is_default: !!f.is_default,
                  description: f.description || '',
                  file: null,
                  filePreview: null,
                };
              }
            }
            return next;
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail || 'Failed to load map details.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mapId, fetchVersion]);

  const flash = useCallback((msg) => {
    setSuccessMessage(msg);
    window.setTimeout(() => {
      setSuccessMessage((cur) => (cur === msg ? null : cur));
    }, 1800);
  }, []);

  const handleFloorsChange = useCallback((next) => {
    setFloors(next);
    setFloorBuffers((prev) => {
      const out = { ...prev };
      const ids = new Set(next.map((f) => f.id));
      for (const key of Object.keys(out)) {
        if (!ids.has(Number(key)) && !ids.has(String(key))) delete out[key];
      }
      for (const f of next) {
        if (!out[f.id]) {
          out[f.id] = {
            name: f.name,
            is_default: !!f.is_default,
            description: f.description || '',
            file: null,
            filePreview: null,
          };
        }
      }
      return out;
    });
  }, []);

  if (loading) {
    return <p className="geo-map-editor-loading">Loading map…</p>;
  }

  return (
    <div className="geo-map-editor">
      <header className="geo-map-editor-head">
        <div>
          <h2>{title}</h2>
          {map?.name && (
            <div className="geo-map-editor-subtitle">
              <span className="geo-map-editor-map-name">{map.name}</span>
              <span className="geo-map-editor-counts">
                {typeof map.level_count === 'number' ? map.level_count : '—'} levels ·{' '}
                {typeof map.floor_count === 'number' ? map.floor_count : floors.length} floors
                {map.game_name ? ` · ${map.game_name}` : ''}
              </span>
            </div>
          )}
        </div>
        {typeof onClose === 'function' && (
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label="Close editor"
          >
            ×
          </button>
        )}
      </header>

      {error && (
        <div
          className="error-message geo-map-editor-banner"
          onClick={() => setError(null)}
          role="button"
          tabIndex={0}
        >
          {error}
          <span className="error-close">×</span>
        </div>
      )}
      {successMessage && (
        <div className="wizard-success geo-map-editor-banner">
          {successMessage}
        </div>
      )}

      <MapDetailsForm
        mapId={mapId}
        initialData={map}
        onMapChange={setMap}
        onError={setError}
        onSuccess={flash}
      />

      <FloorManager
        mapId={mapId}
        initialFloors={floors.length > 0 ? floors : undefined}
        onFloorsChange={handleFloorsChange}
        onError={setError}
        onSuccess={flash}
      />

      <LevelAssigner
        mapId={mapId}
        floors={floors}
        floorBuffers={floorBuffers}
        mapInfo={map}
        enableDraft={enableLevelDraft}
        onError={setError}
        onSuccess={flash}
      />

      {typeof footer === 'function' &&
        footer({
          map,
          floors,
          refresh: () => setFetchVersion((v) => v + 1),
          setError,
          flash,
        })}
    </div>
  );
};

export default GeoMapEditor;
